import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  connectProperty,
  customerLabel,
  fetchConnectTarget,
  fetchGoogleAccounts,
  fetchRestaurantOptions,
  joinNames,
  renameGoogleAccount,
  restaurantLabel,
  type AccountProperty,
  type ConnectRequest,
  type ConnectUndo,
  type GoogleAccount,
  type GoogleAccountsPayload,
  type PropertyFeeds,
  type RestaurantOption,
} from '../../data/googleAccounts';
import { fmtDate } from '../../lib/growthFormat';
import { Icon } from '../shared/Icon';
import { ConnectPropertySheet, ConnectToast, productLabel, type ConnectedToast } from './ConnectPropertySheet';
import '../../styles/account-connect.css';

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** "Feeds Kronenhalle · since 3 Oct 2026, by Michal D." */
export function feedsLine(feeds: PropertyFeeds, t: (key: string, options?: Record<string, unknown>) => string): string {
  const named = feeds.venues.map(v => v.name);
  if (feeds.othersCount) named.push(t('accountConnect.feedsOthers', { count: feeds.othersCount }));
  const venues = joinNames(named, t('accountConnect.and'));
  const date = feeds.since ? fmtDate(feeds.since) : '';
  const by = feeds.by
    ? feeds.by.departed
      ? t('accountConnect.byDeparted')
      : feeds.by.isYou
        ? t('accountConnect.byYou')
        : feeds.by.name || (feeds.by.isStaff ? t('accountConnect.byAleno') : '')
    : '';
  return by
    ? t('accountConnect.stateFeeds', { venues, date, name: by })
    : t('accountConnect.stateFeedsNoBy', { venues, date });
}

type Pair = { key: string; account: GoogleAccount; property: AccountProperty; restaurant: RestaurantOption };

/**
 * "Your Google accounts" (docs/19 section 5), outside any restaurant's
 * context: every Google account the person signed in with, by its name,
 * and every property it sees with its state, to connect to any restaurant
 * they can open. "Review suggestions" lists name matches, every one
 * unticked; "Connect N" first shows what each customer gets.
 */
export function GoogleAccountsPage({
  restaurantId,
  onBack,
}: {
  /** The context's restaurant, which only anchors the calls. */
  restaurantId: string;
  onBack?: () => void;
}) {
  const { t } = useTranslation('growth');
  const [data, setData] = useState<GoogleAccountsPayload | null>(null);
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<ConnectRequest | null>(null);
  const [toast, setToast] = useState<ConnectedToast | null>(null);

  const load = useCallback(async () => {
    try {
      const [accounts, options] = await Promise.all([
        fetchGoogleAccounts(restaurantId),
        fetchRestaurantOptions(),
      ]);
      setData(accounts);
      setRestaurants(options);
      setError(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, [restaurantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dismissToast = useCallback(() => setToast(null), []);

  return (
    <section className="acx-page" aria-labelledby="acx-page-title" data-hj-suppress>
      <header className="acx-page-head">
        {onBack ? (
          <button type="button" className="growth-text-btn growth-back-btn" onClick={onBack}>
            <Icon name="chevron" size={13} />
            {t('accountConnect.back')}
          </button>
        ) : null}
        <h1 id="acx-page-title" className="growth-title">
          {t('accountConnect.pageTitle')}
        </h1>
        <p>{t('accountConnect.pageIntro')}</p>
        <p>{t('accountConnect.onlyYou')}</p>
      </header>

      {error ? (
        <p className="acx-refused" role="alert">
          {error}
        </p>
      ) : null}
      {!data && !error ? (
        <p className="acx-muted" role="status">
          {t('accountConnect.loading')}
        </p>
      ) : null}
      {data && !data.accounts.length ? <p className="acx-muted">{t('accountConnect.empty')}</p> : null}

      {data?.accounts.length ? (
        <ReviewSuggestions
          restaurantId={restaurantId}
          accounts={data.accounts}
          onConnected={next => {
            setToast(next);
            void load();
          }}
        />
      ) : null}

      {data?.accounts.map(account => (
        <AccountCard
          key={account.credentialId}
          restaurantId={restaurantId}
          account={account}
          onRenamed={() => void load()}
          onConnect={(property, initial) =>
            setSheet({
              credentialId: account.credentialId,
              property: { kind: property.kind, externalId: property.externalId, displayName: property.displayName },
              initialRestaurantId: initial,
              suggestions: property.suggestions,
            })
          }
        />
      ))}

      {sheet ? (
        <ConnectPropertySheet
          restaurantId={restaurantId}
          request={sheet}
          restaurants={restaurants}
          onClose={() => setSheet(null)}
          onConnected={next => {
            setSheet(null);
            setToast(next);
            void load();
          }}
        />
      ) : null}
      {toast ? (
        <ConnectToast restaurantId={restaurantId} toast={toast} onDismiss={dismissToast} onUndone={() => void load()} />
      ) : null}
    </section>
  );
}

function AccountCard({
  restaurantId,
  account,
  onRenamed,
  onConnect,
}: {
  restaurantId: string;
  account: GoogleAccount;
  onRenamed: () => void;
  onConnect: (property: AccountProperty, initialRestaurantId: string | null) => void;
}) {
  const { t } = useTranslation('growth');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(account.name || '');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (draft.includes('@')) {
      setError(t('accountConnect.nameNoEmail'));
      return;
    }
    try {
      await renameGoogleAccount(restaurantId, account.credentialId, draft.trim());
      setEditing(false);
      setError(null);
      onRenamed();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const products = account.products.map(productLabel).join(', ');
  return (
    <article className="acx-account" aria-label={account.name || t('accountConnect.unnamed')}>
      <div className="acx-account-head">
        {editing ? (
          <div className="acx-rename">
            <label>
              <span className="acx-pick-label">{t('accountConnect.nameLabel')}</span>{' '}
              <input
                className="acx-input"
                value={draft}
                maxLength={60}
                onChange={e => setDraft(e.target.value)}
                autoFocus
              />
            </label>
            <button type="button" className="acx-primary" onClick={() => void save()}>
              {t('accountConnect.save')}
            </button>
            <button type="button" className="acx-quiet" onClick={() => setEditing(false)}>
              {t('accountConnect.cancel')}
            </button>
          </div>
        ) : (
          <div className="acx-account-name">
            <h2>{account.name || t('accountConnect.unnamed')}</h2>
            <span>
              {products}
              {products ? ' · ' : ''}
              {t('accountConnect.propertyCount', { count: account.properties.length })}
            </span>
          </div>
        )}
        {!editing ? (
          <button type="button" className="acx-quiet" onClick={() => setEditing(true)}>
            {t('accountConnect.rename')}
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="acx-refused" role="alert">
          {error}
        </p>
      ) : null}
      {account.problem === 'reconnect' ? <p className="acx-note">{t('accountConnect.reconnect')}</p> : null}
      <ul className="acx-list">
        {account.properties.map(property => (
          <PropertyRow
            key={`${property.kind}:${property.externalId}`}
            property={property}
            onConnect={initial => onConnect(property, initial)}
          />
        ))}
      </ul>
    </article>
  );
}

export function PropertyRow({
  property,
  onConnect,
  showConnect = true,
}: {
  property: Pick<AccountProperty, 'kind' | 'externalId' | 'displayName' | 'accountLabel' | 'feeds' | 'suggestions'> & {
    state?: AccountProperty['state'];
  };
  onConnect: (initialRestaurantId: string | null) => void;
  showConnect?: boolean;
}) {
  const { t } = useTranslation('growth');
  const state = property.state ?? (property.feeds ? 'feeds' : property.suggestions.length ? 'suggested' : 'none');
  const hint = property.suggestions[0] ?? null;
  const staffLine =
    property.feeds && customerLabel(property.feeds.customerName)
      ? property.feeds.connectionName
        ? t('accountConnect.feedsStaff', {
            customer: customerLabel(property.feeds.customerName),
            connection: property.feeds.connectionName,
          })
        : t('accountConnect.feedsStaffUnnamed', { customer: customerLabel(property.feeds.customerName) })
      : null;
  return (
    <li className="acx-row" data-acx-state={state}>
      <span className="acx-row-name">
        {property.displayName}{' '}
        <span className="acx-row-meta">
          · {productLabel(property.kind)}
          {property.accountLabel ? ` · ${property.accountLabel}` : ''}
        </span>
      </span>
      <span className={`acx-state is-${state}`}>
        {state === 'feeds' && property.feeds
          ? feedsLine(property.feeds, t)
          : state === 'suggested' && hint
            ? t('accountConnect.stateSuggested', { restaurant: restaurantLabel(hint) })
            : t('accountConnect.stateNone')}
        {staffLine ? <span className="acx-state-staff">{staffLine}</span> : null}
      </span>
      {showConnect ? (
        <span className="acx-row-action">
          <button
            type="button"
            className="acx-row-btn"
            aria-label={`${
              state === 'feeds'
                ? t('accountConnect.addRestaurant')
                : state === 'suggested'
                  ? t('accountConnect.connect')
                  : t('accountConnect.chooseRestaurant')
            }: ${property.displayName}`}
            onClick={() => onConnect(state === 'suggested' && hint ? hint.id : null)}
          >
            {state === 'feeds'
              ? t('accountConnect.addRestaurant')
              : state === 'suggested'
                ? t('accountConnect.connect')
                : t('accountConnect.chooseRestaurant')}
          </button>
        </span>
      ) : null}
    </li>
  );
}

type SummaryRow = { pair: Pair; customer: string; refused: string | null; already: boolean };

function ReviewSuggestions({
  restaurantId,
  accounts,
  onConnected,
}: {
  restaurantId: string;
  accounts: GoogleAccount[];
  onConnected: (toast: ConnectedToast) => void;
}) {
  const { t } = useTranslation('growth');
  const pairs = useMemo<Pair[]>(
    () =>
      accounts.flatMap(account =>
        account.properties
          .filter(property => property.state === 'suggested' && property.suggestions.length)
          .map(property => ({
            key: `${account.credentialId}|${property.kind}|${property.externalId}|${property.suggestions[0]!.id}`,
            account,
            property,
            restaurant: property.suggestions[0]!,
          })),
      ),
    [accounts],
  );
  // Every pair starts unticked: a suggestion is a hint, never applied for you.
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<SummaryRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = pairs.filter(pair => ticked[pair.key]);

  const request = (pair: Pair): ConnectRequest => ({
    credentialId: pair.account.credentialId,
    property: {
      kind: pair.property.kind,
      externalId: pair.property.externalId,
      displayName: pair.property.displayName,
    },
  });

  const review = async () => {
    setBusy(true);
    setError(null);
    try {
      const rows = await Promise.all(
        chosen.map(async pair => {
          const target = await fetchConnectTarget(restaurantId, request(pair), pair.restaurant.id);
          const info = target.target;
          return {
            pair,
            customer:
              customerLabel(info?.customerName) ||
              t('accountConnect.summaryCustomerFallback', { restaurant: pair.restaurant.name }),
            refused: info?.feedsOtherCustomer
              ? t('accountConnect.feedsOther', {
                  property: pair.property.displayName,
                  restaurant: pair.restaurant.name,
                })
              : !target.productGranted
                ? t('accountConnect.notGranted', { product: productLabel(pair.property.kind) })
                : null,
            already: Boolean(info?.alreadyRestaurantIds.includes(pair.restaurant.id)),
          };
        }),
      );
      setSummary(rows);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!summary) return;
    setBusy(true);
    setError(null);
    const undo: ConnectUndo[] = [];
    const names: string[] = [];
    try {
      for (const row of summary) {
        if (row.refused || row.already) continue;
        const result = await connectProperty(
          restaurantId,
          request(row.pair),
          [row.pair.restaurant.id],
          row.pair.account.name,
        );
        if (result.undo) undo.push(result.undo);
        names.push(row.pair.restaurant.name);
      }
      setSummary(null);
      setTicked({});
      onConnected({
        restaurant: joinNames(names, t('accountConnect.and')),
        property: chosen.map(pair => pair.property.displayName).join(', '),
        count: names.length,
        undo,
      });
    } catch (e) {
      setError(errorText(e));
      if (undo.length) {
        onConnected({ restaurant: joinNames(names, t('accountConnect.and')), property: '', count: undo.length, undo });
      }
    } finally {
      setBusy(false);
    }
  };

  const groups = useMemo(() => {
    const out = new Map<string, SummaryRow[]>();
    for (const row of summary || []) out.set(row.customer, [...(out.get(row.customer) || []), row]);
    return [...out.entries()];
  }, [summary]);
  const connectable = (summary || []).filter(row => !row.refused && !row.already).length;
  const accountName = chosen[0]?.account.name || t('accountConnect.unnamed');

  return (
    <section className="acx-review" aria-labelledby="acx-review-title">
      <div className="acx-review-head">
        <h2 id="acx-review-title">{t('accountConnect.reviewTitle')}</h2>
        <p className="acx-muted">{t('accountConnect.reviewIntro')}</p>
      </div>
      {!pairs.length ? <p className="acx-muted acx-review-empty">{t('accountConnect.reviewNone')}</p> : null}
      {summary ? (
        <div className="acx-body" role="group" aria-label={t('accountConnect.summaryTitle', { count: connectable })}>
          <h3 className="acx-group-title">{t('accountConnect.summaryTitle', { count: connectable })}</h3>
          {groups.map(([customer, rows]) => (
            <div key={customer} className="acx-summary-group" data-acx="summary-customer">
              <h3>{customer}</h3>
              <ul>
                {rows.map(row => (
                  <li key={row.pair.key} className={row.refused ? 'is-refused' : undefined}>
                    {row.pair.property.displayName} → {restaurantLabel(row.pair.restaurant)}
                    {row.refused ? ` · ${t('accountConnect.summaryRefused', { reason: row.refused })}` : ''}
                    {row.already
                      ? ` · ${t('accountConnect.alreadyUnnamed', { restaurant: row.pair.restaurant.name })}`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="acx-note">{t('accountConnect.summaryNote', { name: accountName })}</p>
        </div>
      ) : (
        <div className="acx-review-list">
          {pairs.map(pair => {
            const on = Boolean(ticked[pair.key]);
            return (
              <label key={pair.key} className={`acx-review-row${on ? ' on' : ''}`}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => setTicked(current => ({ ...current, [pair.key]: !current[pair.key] }))}
                />
                <span>
                  <strong>{pair.property.displayName}</strong>{' '}
                  <span className="acx-arrow" aria-hidden="true">
                    →
                  </span>{' '}
                  {restaurantLabel(pair.restaurant)}
                  <span className="acx-row-meta"> · {pair.account.name || t('accountConnect.unnamed')}</span>
                </span>
              </label>
            );
          })}
        </div>
      )}
      {error ? (
        <p className="acx-refused" role="alert">
          {error}
        </p>
      ) : null}
      {pairs.length ? (
        <div className="acx-review-foot">
          {summary ? (
            <>
              <button type="button" className="acx-quiet" disabled={busy} onClick={() => setSummary(null)}>
                {t('accountConnect.summaryBack')}
              </button>
              <button
                type="button"
                className="acx-primary"
                disabled={busy || !connectable}
                onClick={() => void confirm()}
              >
                {busy ? t('accountConnect.connecting') : t('accountConnect.connectN', { count: connectable })}
              </button>
            </>
          ) : (
            <>
              <span />
              <button
                type="button"
                className="acx-primary"
                disabled={busy || !chosen.length}
                onClick={() => void review()}
              >
                {busy ? t('accountConnect.checking') : t('accountConnect.connectN', { count: chosen.length })}
              </button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
